import { useState } from 'react';
import type { JustPulledItem } from '../../../modules/api';
import { CardModal, Image, Skeleton } from '../../../modules/design-system';

type Props = {
  items: JustPulledItem[];
  loading: boolean;
  /** The label leads to the Just Pulled page; without it the label is text. */
  onShowAll?: () => void;
};

const SKELETON_COUNT = 3;

/**
 * The pack's latest pulls in a row (Figma 19074:253777, 19101:336895): a
 * glass chip per card — its slab and its name on two lines. A chip opens
 * the card. Nothing pulled yet — no row.
 */
export function JustPulledStrip({ items, loading, onShowAll }: Props) {
  const [selected, setSelected] = useState<JustPulledItem | null>(null);

  if (!loading && items.length === 0) return null;

  return (
    <section aria-label="Just pulled" className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onShowAll}
        disabled={!onShowAll}
        className="self-start text-[13px] font-medium leading-[19px] [color:var(--glass-text-muted)] enabled:cursor-pointer enabled:hover:text-white"
      >
        Just pulled
      </button>
      <div className="scrollbar-hide flex gap-2 overflow-x-auto">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelected(item)}
            className="flex h-12 w-[228px] shrink-0 cursor-pointer items-center gap-2 rounded-[16px] px-2 text-left [background:var(--glass-pill-fill)] transition-[filter] hover:brightness-125"
          >
            <Image
              src={item.imageUrl}
              alt=""
              className="relative h-9 w-6 shrink-0 rounded-[3px]"
              imgClassName="absolute inset-0 h-full w-full object-contain"
              width={24}
              height={36}
            />
            <span className="line-clamp-2 text-[12px] leading-4 text-white">{item.title}</span>
          </button>
        ))}
        {loading &&
          Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <Skeleton key={i} className="h-12 w-[228px] shrink-0 rounded-[16px]" />
          ))}
      </div>
      <CardModal open={selected !== null} onClose={() => setSelected(null)} item={selected} label="Just Pulled" />
    </section>
  );
}
